// UTILS
import { nextTick } from "vue"
import { setup, createActor, fromPromise } from "xstate"
import { storeToRefs } from "pinia"
import router from "@/router"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { COOKIES } from "@/config/constants"
import { useSessionStorage } from "@/helpers/useSessionStorage"

// TYPES
import type { GlobalMachineEvent } from "@/machines/types"
import type { VoiceMachineEventType } from "./types"

// STORES
import { useUserStore } from "@/stores/user"
import { useAnswersStore } from "@/features/voice/store/answers.store"
import { useQuestionsStore } from "@/features/voice/store/questions.store"

// MACHINE
import { globalActor } from "@/machines/globalMachine"

// Loader for Voice datas
// to redirect to Intro or WelcomeBack
const loadVoiceData = fromPromise(async () => {
  const { fetchVoice, retakeTest } = useAnswersStore()
  const voice = await fetchVoice()
  const { fetchQuestions, getNextQuestion } = useQuestionsStore()

  const assessmentSummary = useUserStore().assessmentSummary.assessments.swipe
  const isFinisedAndNotPassed =
    voice?.status === "finish" && assessmentSummary?.not_passed

  // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
  let assessmentStatus = isFinisedAndNotPassed ? "todo" : voice?.status

  // status can be "todo", "inprogress" or "finish"
  switch (assessmentStatus) {
    case "todo":
      await fetchQuestions()
      await getNextQuestion()
      return { status: "new" }

    case "inprogress":
      await fetchQuestions()
      await getNextQuestion()
      return { status: "showQuestions" }

    case "redirect-contact":
      globalActor.send({ type: "GLOBAL.GUEST.CONTACTSUPPORT" })
      return

    case "finish":
      if (
        voice?.can_restart ||
        useUserStore().nextAssessment?.next_assessment?.retake
      ) {
        await retakeTest()
        await fetchQuestions()
        await getNextQuestion()
        return { status: "new" }
      }
      return { status: "showFinal" }

    default:
      if (useUserStore().nextAssessment?.next_assessment?.retake) {
        await retakeTest()
        await fetchQuestions()
        await getNextQuestion()
        return { status: "new" }
      }
      await fetchQuestions()
      await getNextQuestion()
      return { status: "new" }
  }
})

export const voiceMachine = setup({
  actors: {
    loadVoiceData,
  },
  types: {} as {
    events: VoiceMachineEventType
  },
  actions: {},
}).createMachine({
  id: "voiceMachine",
  initial: "loading",
  states: {
    loading: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "voiceLoading") {
            router.replace({ name: "voiceLoading" })
          }
        },
      ],
      invoke: {
        id: "loadVoice",
        src: "loadVoiceData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "intro",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    intro: {
      entry: [() => router.replace({ name: "voiceIntro" })],
      on: {
        done: { target: "rules" },
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "voiceWelcomeBack" })],
      on: {
        done: { target: "authorize" },
        test: { target: "test" },
      },
    },
    rules: {
      entry: [() => router.replace({ name: "voiceRules" })],
      on: {
        done: { target: "authorize" },
        tuto: { target: "tuto" },
        back: { target: "intro" },
      },
    },
    authorize: {
      entry: [() => router.replace({ name: "voiceAuthorize" })],
      on: {
        done: { target: "tuto" },
        test: { target: "test" },
        refuse: { target: "authorizeRefused" },
        back: { target: "rules" },
      },
    },
    authorizeRefused: {
      entry: [() => router.replace({ name: "voiceAuthorizeRefused" })],
      on: {
        back: { target: "authorize" },
        done: { target: "final" },
      },
    },
    tuto: {
      entry: [() => router.replace({ name: "voiceTuto" })],
      on: {
        done: { target: "test" },
        retry: { target: "authorize" },
      },
    },
    test: {
      entry: [() => router.replace({ name: "voiceTest" })],
      on: {
        done: { target: "feedback" },
      },
    },
    feedback: {
      entry: [() => router.replace({ name: "voiceFeedback" })],
      on: {
        done: { target: "end" },
      },
    },
    end: {
      entry: [() => router.replace({ name: "voiceEnd" })],
      on: {
        done: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "voiceLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },

    final: {
      type: "final",
    },
  },
})

export const voiceActor = createActor(voiceMachine)

voiceActor.subscribe({
  async complete() {
    const { isFromInt } = storeToRefs(useUserStore())
    const { getSessionItem, deleteSessionItem } = useSessionStorage()

    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())

    const workflow_uuid = useCookies().get(COOKIES.WORKFLOW_UUID)
    await fetchNextAssessment(workflow_uuid ?? null)

    await nextTick()

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.WORKFLOW_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.PM_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.COMPANY_UUID, COOKIES.OPTIONS)

      if (isFromInt.value) {
        if (getSessionItem("goto_param")) {
          deleteSessionItem("goto_param")
        }
        if (getSessionItem("redirect_url")) {
          let redirectUrl = getSessionItem("redirect_url") as string
          deleteSessionItem("redirect_url")
          // Redirect to custom page
          window.location.href = redirectUrl
          return
        } else {
          // Redirect to thanksATS route
          router.push({ name: "thanksATS" })
          return
        }
      }

      globalActor.send({ type: "GLOBAL.VOICE.DONE" })
    }
  },
})
